import { XMLParser } from 'fast-xml-parser';
import type { MultiPoligono, Anillo } from '../geo/geometria';

/**
 * Parseo del GML INSPIRE del Catastro (WFS 2.0) a registros planos.
 *
 * Estructura real (comprobada con las muestras de `../../muestras/`):
 * - Parcelas: <FeatureCollection><member><cp:CadastralParcel gml:id="ES.SDGC.CP.{rc}">
 *   con cp:areaValue, cp:label, cp:nationalCadastralReference, cp:referencePoint y
 *   cp:geometry/gml:MultiSurface/gml:surfaceMember/gml:Surface/gml:patches/gml:PolygonPatch.
 * - Edificios: <gml:featureMember><bu-ext2d:Building gml:id="ES.SDGC.BU.{rc}"> con currentUse,
 *   conditionOfConstruction, dateOfConstruction/DateOfEvent/beginning, numberOfDwellings,
 *   numberOfBuildingUnits, numberOfFloorsAboveGround (a menudo nil), officialArea/OfficialArea/value
 *   (grossFloorArea) y geometry/BuildingGeometry/geometry/gml:Surface.
 * - Partes: <bu-ext2d:BuildingPart gml:id="ES.SDGC.BU.{rc}_part{n}"> con numberOfFloorsAboveGround,
 *   numberOfFloorsBelowGround, heightBelowGround y la misma geometria.
 * Los prefijos de espacio de nombres se eliminan al parsear, asi que el codigo trabaja con los
 * nombres locales. Las coordenadas vienen como "x y" en el huso pedido; por si acaso se detecta el
 * orden por magnitud (una northing UTM en Espana supera los 3.900.000).
 */

export interface ParcelaGml {
  rc: string;
  etiqueta: string | null;
  areaM2: number | null;
  geom: MultiPoligono;
}

export interface EdificioGml {
  rc: string;
  uso: string | null;
  estado: string | null;
  anio: number | null;
  plantas: number | null;
  unidades: number | null;
  viviendas: number | null;
  supConstruidaM2: number | null;
  fotoUrl: string | null;
  geom: MultiPoligono;
}

export interface ParteGml {
  id: string;
  rc: string;
  plantasSobre: number | null;
  plantasBajo: number | null;
  alturaBajoM: number | null;
  estado: string | null;
  geom: MultiPoligono;
}

export interface ColeccionGml<T> {
  registros: T[];
  epsg: number | null;
}

type Nodo = Record<string, unknown>;

const parser = new XMLParser({
  ignoreAttributes: false,
  attributeNamePrefix: '@_',
  removeNSPrefix: true,
  parseTagValue: false,
  parseAttributeValue: false,
  trimValues: true,
  isArray: (nombre) =>
    ['member', 'featureMember', 'surfaceMember', 'interior', 'CadastralParcel', 'Building', 'BuildingPart', 'Surface'].includes(nombre),
});

/** Decodifica la respuesta respetando la declaracion XML (el WFS de parcelas declara ISO-8859-1). */
export function decodificarXml(datos: ArrayBuffer): string {
  const cabecera = new TextDecoder('latin1').decode(datos.slice(0, 200));
  const m = /encoding="([^"]+)"/i.exec(cabecera);
  const codificacion = m ? m[1].toLowerCase() : 'utf-8';
  try {
    return new TextDecoder(codificacion).decode(datos);
  } catch {
    return new TextDecoder('utf-8').decode(datos);
  }
}

/** Devuelve el texto de un ows:ExceptionReport, o null si la respuesta no es una excepcion. */
export function detectarExcepcion(xml: string): string | null {
  if (!/ExceptionReport/.test(xml.slice(0, 2000))) return null;
  const m = /<(?:ows:)?ExceptionText>([\s\S]*?)<\/(?:ows:)?ExceptionText>/i.exec(xml);
  const codigo = /exceptionCode="([^"]+)"/i.exec(xml);
  return (m ? m[1].trim() : 'ExceptionReport sin texto') + (codigo ? ` (${codigo[1]})` : '');
}

function esNodo(v: unknown): v is Nodo {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Recorre el arbol y devuelve todos los valores bajo la clave indicada (aplanando arrays). */
export function recolectar(raiz: unknown, clave: string, salida: unknown[] = []): unknown[] {
  if (Array.isArray(raiz)) {
    for (const v of raiz) recolectar(v, clave, salida);
  } else if (esNodo(raiz)) {
    for (const [k, v] of Object.entries(raiz)) {
      if (k === clave) {
        if (Array.isArray(v)) salida.push(...v); else salida.push(v);
      } else if (typeof v === 'object' && v !== null) {
        recolectar(v, clave, salida);
      }
    }
  }
  return salida;
}

/** Texto de un valor que puede ser string, objeto con #text o nil. */
export function texto(v: unknown): string | null {
  if (v === undefined || v === null) return null;
  if (typeof v === 'string') return v.length ? v : null;
  if (typeof v === 'number') return String(v);
  if (esNodo(v)) {
    if (v['@_nil'] === 'true') return null;
    const t = v['#text'];
    if (typeof t === 'string') return t.length ? t : null;
    if (typeof t === 'number') return String(t);
  }
  return null;
}

export function numero(v: unknown): number | null {
  const t = texto(v);
  if (t === null) return null;
  const n = Number(t.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

export function entero(v: unknown): number | null {
  const n = numero(v);
  return n === null ? null : Math.round(n);
}

function anioDe(v: unknown): number | null {
  const t = texto(v);
  if (!t) return null;
  const m = /^(\d{4})/.exec(t);
  if (!m) return null;
  const a = Number(m[1]);
  return a > 1000 && a < 2200 ? a : null;
}

export function primero(raiz: unknown, clave: string): unknown {
  const v = recolectar(raiz, clave);
  return v.length ? v[0] : undefined;
}

export function epsgDeSrs(srs: unknown): number | null {
  const t = texto(srs);
  if (!t) return null;
  const m = /EPSG.*?(\d{4,5})\s*$/i.exec(t);
  return m ? Number(m[1]) : null;
}

function anilloDePosList(posList: unknown): Anillo {
  const t = texto(posList);
  if (!t) return [];
  const nums = t.split(/\s+/).filter((s) => s.length).map(Number);
  const anillo: Anillo = [];
  for (let i = 0; i + 1 < nums.length; i += 2) anillo.push([nums[i], nums[i + 1]]);
  // Orden de ejes: si el primer valor parece una northing, se intercambia.
  if (anillo.length && anillo[0][0] > 3_000_000 && anillo[0][1] < 3_000_000) {
    for (const p of anillo) { const x = p[0]; p[0] = p[1]; p[1] = x; }
  }
  // GML cierra el anillo repitiendo el primer vertice; se elimina para trabajar con anillos abiertos.
  if (anillo.length > 1) {
    const a = anillo[0], z = anillo[anillo.length - 1];
    if (a[0] === z[0] && a[1] === z[1]) anillo.pop();
  }
  return anillo;
}

/** Extrae los poligonos de cualquier nodo de geometria (MultiSurface o Surface sueltas). */
function geometriaDe(nodoGeometria: unknown): MultiPoligono {
  const superficies = recolectar(nodoGeometria, 'Surface');
  const salida: MultiPoligono = [];
  for (const s of superficies) {
    const parches = recolectar(s, 'PolygonPatch');
    for (const parche of parches) {
      if (!esNodo(parche)) continue;
      const exterior = anilloDePosList(primero(parche['exterior'], 'posList'));
      if (exterior.length < 3) continue;
      const poligono: Anillo[] = [exterior];
      const interiores = parche['interior'];
      if (Array.isArray(interiores)) {
        for (const h of interiores) {
          const anillo = anilloDePosList(primero(h, 'posList'));
          if (anillo.length >= 3) poligono.push(anillo);
        }
      }
      salida.push(poligono);
    }
  }
  return salida;
}

function epsgDeColeccion(raiz: unknown): number | null {
  const srs = recolectar(raiz, '@_srsName');
  for (const s of srs) {
    const e = epsgDeSrs(s);
    if (e) return e;
  }
  return null;
}

function rcDeId(id: string | null, prefijo: RegExp): string | null {
  if (!id) return null;
  const m = prefijo.exec(id);
  return m ? m[1] : null;
}

function parsear(xml: string): unknown {
  const excepcion = detectarExcepcion(xml);
  if (excepcion) throw new Error(`El WFS del Catastro devolvió una excepción: ${excepcion}`);
  return parser.parse(xml);
}

export function parsearParcelas(xml: string): ColeccionGml<ParcelaGml> {
  const raiz = parsear(xml);
  const registros: ParcelaGml[] = [];
  for (const n of recolectar(raiz, 'CadastralParcel')) {
    if (!esNodo(n)) continue;
    const rc = texto(n['nationalCadastralReference']) ?? rcDeId(texto(n['@_id']), /ES\.SDGC\.CP\.([A-Z0-9]{14})/i);
    if (!rc) continue;
    registros.push({
      rc,
      etiqueta: texto(n['label']),
      areaM2: numero(n['areaValue']),
      geom: geometriaDe(n['geometry']),
    });
  }
  return { registros, epsg: epsgDeColeccion(raiz) };
}

export function parsearEdificios(xml: string): ColeccionGml<EdificioGml> {
  const raiz = parsear(xml);
  const registros: EdificioGml[] = [];
  for (const n of recolectar(raiz, 'Building')) {
    if (!esNodo(n)) continue;
    const rc = rcDeId(texto(n['@_id']), /ES\.SDGC\.BU\.([A-Z0-9]{14})/i)
      ?? texto(primero(n['externalReference'], 'reference'));
    if (!rc) continue;
    registros.push({
      rc,
      uso: texto(n['currentUse']),
      estado: texto(n['conditionOfConstruction']),
      anio: anioDe(primero(n['dateOfConstruction'], 'beginning')),
      plantas: entero(n['numberOfFloorsAboveGround']),
      unidades: entero(n['numberOfBuildingUnits']),
      viviendas: entero(n['numberOfDwellings']),
      supConstruidaM2: numero(primero(n['officialArea'], 'value')),
      fotoUrl: texto(primero(n['document'], 'documentLink')),
      geom: geometriaDe(n['geometry']),
    });
  }
  return { registros, epsg: epsgDeColeccion(raiz) };
}

export function parsearPartes(xml: string): ColeccionGml<ParteGml> {
  const raiz = parsear(xml);
  const registros: ParteGml[] = [];
  for (const n of recolectar(raiz, 'BuildingPart')) {
    if (!esNodo(n)) continue;
    const id = texto(n['@_id']) ?? texto(primero(n['inspireId'], 'localId'));
    if (!id) continue;
    const rc = rcDeId(id, /([A-Z0-9]{14})_part/i) ?? rcDeId(id, /ES\.SDGC\.BU\.([A-Z0-9]{14})/i);
    if (!rc) continue;
    registros.push({
      id: id.replace(/^ES\.SDGC\.BU\./, ''),
      rc,
      plantasSobre: entero(n['numberOfFloorsAboveGround']),
      plantasBajo: entero(n['numberOfFloorsBelowGround']),
      alturaBajoM: numero(n['heightBelowGround']),
      estado: texto(n['conditionOfConstruction']),
      geom: geometriaDe(n['geometry']),
    });
  }
  return { registros, epsg: epsgDeColeccion(raiz) };
}

/** Usos del Catastro INSPIRE traducidos para la interfaz. */
export const USOS: Record<string, string> = {
  '1_residential': 'Residencial',
  '2_agriculture': 'Agrario',
  '3_industrial': 'Industrial',
  '4_1_office': 'Oficinas',
  '4_2_retail': 'Comercial',
  '4_3_publicServices': 'Servicios públicos',
};

export const ESTADOS: Record<string, string> = {
  functional: 'En uso',
  declined: 'Deteriorado',
  ruin: 'Ruina',
  projected: 'Proyectado',
  underConstruction: 'En construcción',
  demolished: 'Demolido',
};

export function nombreUso(uso: string | null): string {
  return uso ? USOS[uso] ?? uso : 'Sin dato';
}

export function nombreEstado(estado: string | null): string {
  return estado ? ESTADOS[estado] ?? estado : 'Sin dato';
}
